import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Logo } from "../components/Logo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { useAuth } from "../lib/auth";
import { Loader2, CheckCircle2 } from "lucide-react";
import { track, identify, EVENTS } from "../lib/analytics";

export default function Signup() {
  const { signup, user } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (user) navigate(user.onboarding_complete ? "/dashboard" : "/onboarding", { replace: true });
  }, [user, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setLoading(true);
    try {
      const u = await signup(email, password, name);
      toast.success("Account created — let's set up the first check-in.");
      if (u?.id) identify(u.id, { email: u.email, plan: "trial" });
      track(EVENTS.SIGNUP_COMPLETED, { source: "web" });
      navigate("/onboarding", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Signup failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 cherishlink-gradient-bg">
      <div className="hidden lg:flex flex-col justify-between p-12 relative overflow-hidden" style={{ background: "linear-gradient(135deg, hsl(212 73% 96%) 0%, hsl(177 45% 96%) 100%)" }}>
        <Logo to="/" size="lg" />
        <div className="relative z-10">
          <h2 className="font-display text-4xl font-bold tracking-tight leading-tight text-foreground">
            Start your 7-day free trial.
          </h2>
          <ul className="mt-6 space-y-3 text-muted-foreground">
            {["No credit card required", "No app for your parent", "Cancel anytime"].map((t) => (
              <li key={t} className="flex gap-2 items-center">
                <CheckCircle2 className="h-5 w-5 text-secondary" /> {t}
              </li>
            ))}
          </ul>
        </div>
        <div className="text-xs text-muted-foreground/70">© {new Date().getFullYear()} CherishLink</div>
      </div>
      <div className="flex items-center justify-center p-6 md:p-12">
        <Card className="w-full max-w-md rounded-3xl border-border/60 soft-shadow">
          <CardContent className="p-8 md:p-10">
            <div className="lg:hidden mb-8"><Logo /></div>
            <h1 className="font-display text-3xl font-bold tracking-tight">Create your account</h1>
            <p className="mt-2 text-muted-foreground">Peace of mind in under 2 minutes.</p>

            <form onSubmit={onSubmit} className="mt-8 space-y-5">
              <div>
                <Label htmlFor="name">Your name</Label>
                <Input id="name" required data-testid="signup-name-input"
                  value={name} onChange={(e) => setName(e.target.value)}
                  className="mt-2 h-11 rounded-xl" placeholder="Sarah Johnson" />
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" required data-testid="signup-email-input"
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  className="mt-2 h-11 rounded-xl" placeholder="you@example.com" />
              </div>
              <div>
                <Label htmlFor="password">Password</Label>
                <Input id="password" type="password" required data-testid="signup-password-input"
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  className="mt-2 h-11 rounded-xl" placeholder="At least 6 characters" />
              </div>
              <Button type="submit" disabled={loading} className="w-full h-11 rounded-full" data-testid="signup-submit-button">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create account"}
              </Button>
            </form>

            <p className="mt-8 text-sm text-center text-muted-foreground">
              Already have an account?{" "}
              <Link to="/login" className="text-primary font-medium hover:underline" data-testid="signup-to-login">
                Sign in
              </Link>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
